import React from 'react';
import { Share2, CheckCircle, Users, Target, TrendingUp } from 'lucide-react';

export default function InfluencerMarketing() {
  const framework = [
    {
      phase: 'Phase 1: Creator Mix Blueprint',
      duration: 'Weeks 1-2',
      activities: [
        'Audience and competitor scan to benchmark creator archetypes',
        'Creator stack design across macro, micro, and community voices',
        'UGC capture brief that codifies brand voice and product proof points',
        'Offer, incentive, and usage rights modeling for each creator tier'
      ]
    },
    {
      phase: 'Phase 2: Relationship Orchestration',
      duration: 'Weeks 3-4',
      activities: [
        'Outreach sequences with value-first positioning and story hooks',
        'Creator onboarding workflows covering compliance and brand guardrails',
        'Product seeding logistics, tracking, and feedback loops',
        'Shared dashboards to visualize content pipeline and deliverable status'
      ]
    },
    {
      phase: 'Phase 3: Content Flywheel',
      duration: 'Weeks 5-8',
      activities: [
        'UGC studio that edits raw creator footage into high-performing variations',
        'Channel-specific formatting for TikTok, Reels, Shorts, and paid placements',
        'Storyline and hook testing matrix to identify conversion drivers',
        'Rights management and evergreen library tagging for future campaigns'
      ]
    },
    {
      phase: 'Phase 4: Amplify & Optimize',
      duration: 'Ongoing',
      activities: [
        'Always-on performance review across awareness, engagement, and CAC',
        'Paid amplification playbooks for top-performing creator assets',
        'Affiliate layering and promo calendar optimization',
        'Quarterly creator council to refresh insight and co-create launches'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'UGC Flywheel Engine',
      icon: Share2,
      description: 'We turn every creator partnership into licensed UGC that fuels organic, paid, and retention channels—no more brilliant videos trapped on a single feed.'
    },
    {
      title: 'Creator CRM',
      icon: Users,
      description: 'Centralized creator profiles with contract status, performance notes, and next-touch actions mean no relationship goes cold and no deliverable slips.'
    },
    {
      title: 'Conversion Lab',
      icon: Target,
      description: 'Testing sprints isolate the hooks, angles, and offers that move product. We replicate winners across segments while killing creative fatigue fast.'
    },
    {
      title: 'Revenue Accountability',
      icon: TrendingUp,
      description: 'Dashboards connect influencer spend to attributable revenue, CAC, LTV lift, and retention impact so budget conversations stay data-backed.'
    }
  ];

  const deliverables = [
    'Creator pipeline with 60+ vetted prospects mapped to your ICP',
    'Quarterly UGC library with file naming, licensing rights, and usage notes',
    'Campaign scorecard covering reach, engagement, assisted revenue, and insights',
    'Playbooks for product launches, gifting waves, and ambassador programs'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Share2 size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Influencer & UGC Marketing</h1>
              <p className="text-xl text-gray-600 mt-2">Scale trusted storytelling with a measurable creator engine</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Creator Growth Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            We architect influencer programs that compound—turning creators into constant collaborators, transforming UGC into evergreen assets, and tying every deliverable to commercial outcomes.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Traditional influencer campaigns chase vanity metrics. We design a system where every creator builds trust, every asset becomes reusable UGC, and every collaboration advances revenue goals.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Program Deliverables</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Launch Your UGC Engine?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let’s design a creator program that delivers measurable demand and reusable content from day one.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Book%20a%20Strategy%20Session&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Book a Strategy Session
          </a>
        </div>
      </div>
    </div>
  );
}
